import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { ProtectedRoute } from './components/ProtectedRoute'
import { ProfileGuardedRoute } from './components/ProfileGuardedRoute'
import { AdminRoute } from './components/AdminRoute'
import { WorkshopGuardedRoute } from './components/WorkshopGuardedRoute'
import { ScrollToTop } from './components/ScrollToTop'
import { LoadingSpinner } from './components/LoadingSpinner'
import { ErrorBoundary } from './components/ErrorBoundary'
import { usePageTracking } from './hooks/usePageTracking'

// Keep critical auth flow components eagerly loaded for better UX
import { Login } from './pages/Login'
import { Signup } from './pages/Signup'
import { ResetPassword } from './pages/ResetPassword'

// Lazy load all other page components
const GuestRegistration = lazy(() => import('./pages/GuestRegistration'))
const Dashboard = lazy(() => 
  import('./pages/Dashboard').then(module => ({ default: module.Dashboard }))
    .catch(error => {
      console.error('🔴 Failed to load Dashboard component:', error);
      // Return a fallback component in case of load failure
      return { 
        default: () => (
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-foreground mb-4">Loading Error</h1>
              <p className="text-muted-foreground mb-4">Failed to load the dashboard. Please refresh the page.</p>
              <button 
                onClick={() => window.location.reload()} 
                className="bg-primary text-primary-foreground px-4 py-2 rounded hover:bg-primary/90"
              >
                Refresh Page
              </button>
            </div>
          </div>
        )
      };
    })
)
const Tools = lazy(() => import('./pages/Tools').then(module => ({ default: module.Tools })))
const FocusFinder = lazy(() => import('./pages/FocusFinder').then(module => ({ default: module.FocusFinder })))
const Vision = lazy(() => import('./pages/Vision').then(module => ({ default: module.Vision })))
const TwoOars = lazy(() => import('./pages/TwoOars').then(module => ({ default: module.TwoOarsPage })))
const VisionPlanPage = lazy(() => import('./pages/VisionPlan').then(module => ({ default: module.VisionPlanPage })))
const VisionPlan2Page = lazy(() => import('./pages/VisionPlan2').then(module => ({ default: module.VisionPlan2Page })))
const CustomerAvatarBuilder = lazy(() => import('./pages/CustomerAvatarBuilder').then(module => ({ default: module.CustomerAvatarBuilder })))
const CashflowPlan = lazy(() => import('./pages/CashflowPlan').then(module => ({ default: module.CashflowPlan })))
const BusinessModelAnalysis = lazy(() => import('./pages/BusinessModelAnalysis').then(module => ({ default: module.BusinessModelAnalysis })))
const ProblemSizeMatrix = lazy(() => import('./pages/ProblemSizeMatrix').then(module => ({ default: module.ProblemSizeMatrix })))
const CustomerAcquisitionModel = lazy(() => import('./pages/CustomerAcquisitionModel').then(module => ({ default: module.CustomerAcquisitionModel })))
const RiceModelRiskRatio = lazy(() => import('./pages/RiceModelRiskRatio').then(module => ({ default: module.RiceModelRiskRatio })))
const BottomUpFinancialProjections = lazy(() => import('./pages/BottomUpFinancialProjections').then(module => ({ default: module.BottomUpFinancialProjections })))
const WeeklyTeamCompass = lazy(() => import('./pages/WeeklyTeamCompass').then(module => ({ default: module.WeeklyTeamCompass })))
const ThirtyDaySprintTool = lazy(() => import('./pages/ThirtyDaySprintTool').then(module => ({ default: module.ThirtyDaySprintTool })))
const DailyTeamCheckInsTool = lazy(() => import('./pages/DailyTeamCheckInsTool').then(module => ({ default: module.DailyTeamCheckInsTool })))
const WeeklyLeadershipSyncTool = lazy(() => import('./pages/WeeklyLeadershipSyncTool').then(module => ({ default: module.WeeklyLeadershipSyncTool })))
const CompanyCreedTool = lazy(() => import('./pages/CompanyCreedTool').then(module => ({ default: module.CompanyCreedTool })))
const CompanyGoalsProcessTool = lazy(() => import('./pages/CompanyGoalsProcessTool').then(module => ({ default: module.CompanyGoalsProcessTool })))
const DuplicateYourselfTool = lazy(() => import('./pages/DuplicateYourselfTool').then(module => ({ default: module.DuplicateYourselfTool })))

const TopPerformersCheetahVsCatTool = lazy(() => import('./pages/TopPerformersCheetahVsCatTool').then(module => ({ default: module.TopPerformersCheetahVsCatTool })))
const ContrarianInterviewingTool = lazy(() => import('./pages/ContrarianInterviewingTool').then(module => ({ default: module.ContrarianInterviewingTool })))
const UtilizingVATool = lazy(() => import('./pages/UtilizingVATool').then(module => ({ default: module.UtilizingVATool })))
const JobDescriptionTool = lazy(() => import('./pages/JobDescriptionTool').then(module => ({ default: module.JobDescriptionTool })))
const HundredDayPlanTool = lazy(() => import('./pages/HundredDayPlanTool').then(module => ({ default: module.HundredDayPlanTool })))
const RoleResultsTool = lazy(() => import('./pages/RoleResultsTool').then(module => ({ default: module.RoleResultsTool })))
const OrgChartTool = lazy(() => import('./pages/OrgChartTool').then(module => ({ default: module.OrgChartTool })))
const SetWorkingCultureTool = lazy(() => import('./pages/SetWorkingCultureTool').then(module => ({ default: module.SetWorkingCultureTool })))
const QuarterlyGoalBuilderTool = lazy(() => import('./pages/QuarterlyGoalBuilderTool').then(module => ({ default: module.QuarterlyGoalBuilderTool })))
const CEOMissionControlDashboard = lazy(() => import('./pages/CEOMissionControlDashboard').then(module => ({ default: module.CEOMissionControlDashboard })))
const ProjectsDatabaseTool = lazy(() => import('./pages/ProjectsDatabaseTool').then(module => ({ default: module.ProjectsDatabaseTool })))
const SOPBuilderDatabaseTool = lazy(() => import('./pages/SOPBuilderDatabaseTool').then(module => ({ default: module.SOPBuilderDatabaseTool })))
const TaskDatabaseOutlineTool = lazy(() => import('./pages/TaskDatabaseOutlineTool').then(module => ({ default: module.TaskDatabaseOutlineTool })))
const TechStackEvaluatorTool = lazy(() => import('./pages/TechStackEvaluatorTool').then(module => ({ default: module.TechStackEvaluatorTool })))
const FlywheelCreationTool = lazy(() => import('./pages/FlywheelCreationTool').then(module => ({ default: module.FlywheelCreationTool })))
const PricingFrameworkTool = lazy(() => import('./pages/PricingFrameworkTool').then(module => ({ default: module.PricingFrameworkTool })))
const SalesEnablementLibraryTool = lazy(() => import('./pages/SalesEnablementLibraryTool').then(module => ({ default: module.SalesEnablementLibraryTool })))
const SalesPipelineProcessTool = lazy(() => import('./pages/SalesPipelineProcessTool').then(module => ({ default: module.SalesPipelineProcessTool })))
const ProductTickSheetTool = lazy(() => import('./pages/ProductTickSheetTool').then(module => ({ default: module.ProductTickSheetTool })))
const ObjectionRebuttalMatrixTool = lazy(() => import('./pages/ObjectionRebuttalMatrixTool').then(module => ({ default: module.ObjectionRebuttalMatrixTool })))
const FAQMatrixTool = lazy(() => import('./pages/FAQMatrixTool').then(module => ({ default: module.FAQMatrixTool })))
const GrowthMarketingFormulaTool = lazy(() => import('./pages/GrowthMarketingFormulaTool').then(module => ({ default: module.GrowthMarketingFormulaTool })))
const CashflowAccelerationTool = lazy(() => import('./pages/CashflowAccelerationTool').then(module => ({ default: module.CashflowAccelerationTool })))
const ZeroBasedBudgetingTool = lazy(() => import('./pages/ZeroBasedBudgetingTool').then(module => ({ default: module.ZeroBasedBudgetingTool })))
const VendorDiscountsTool = lazy(() => import('./pages/VendorDiscountsTool').then(module => ({ default: module.VendorDiscountsTool })))
const DynamicPLTool = lazy(() => import('./pages/DynamicPLTool').then(module => ({ default: module.DynamicPLTool })))
const DynamicFinancialDashboard = lazy(() => import('./pages/DynamicFinancialDashboard').then(module => ({ default: module.DynamicFinancialDashboard })))
const Models = lazy(() => import('./pages/Models').then(module => ({ default: module.Models })))
const Tempo = lazy(() => import('./pages/Tempo').then(module => ({ default: module.Tempo })))
const People = lazy(() => import('./pages/People').then(module => ({ default: module.People })))
const Ops = lazy(() => import('./pages/Ops').then(module => ({ default: module.Ops })))
const MarketingSales = lazy(() => import('./pages/MarketingSales').then(module => ({ default: module.MarketingSales })))
const Finance = lazy(() => import('./pages/Finance').then(module => ({ default: module.Finance })))
const Settings = lazy(() => import('./pages/Settings').then(module => ({ default: module.Settings })))
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(module => ({ default: module.AdminDashboard })))
const AdminErrorReports = lazy(() => import('./pages/AdminErrorReports').then(module => ({ default: module.AdminErrorReports })))
const AdminAnalytics = lazy(() => import('./pages/AdminAnalytics').then(module => ({ default: module.AdminAnalytics })))
const CourseManagement = lazy(() => import('./pages/CourseManagement').then(module => ({ default: module.CourseManagement })))
const CourseForm = lazy(() => import('./pages/CourseForm').then(module => ({ default: module.CourseForm })))
const CourseDetails = lazy(() => import('./pages/CourseDetails').then(module => ({ default: module.CourseDetails })))
const CourseViewer = lazy(() => import('./pages/CourseViewer').then(module => ({ default: module.CourseViewer })))
const CourseNotes = lazy(() => import('./pages/CourseNotes').then(module => ({ default: module.CourseNotes })))
const Courses = lazy(() => import('./pages/Courses').then(module => ({ default: module.Courses })))
const VideoManager = lazy(() => import('./pages/VideoManager').then(module => ({ default: module.VideoManager })))
const VideoVault = lazy(() => import('./pages/VideoVault').then(module => ({ default: module.VideoVault })))
const Changelog = lazy(() => import('./pages/Changelog').then(module => ({ default: module.Changelog })))
const PrivacyStatement = lazy(() => import('./pages/PrivacyStatement').then(module => ({ default: module.PrivacyStatement })))
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(module => ({ default: module.TermsOfService })))
const Support = lazy(() => import('./pages/Support').then(module => ({ default: module.Support })))
const FeatureRequests = lazy(() => import('./pages/FeatureRequests').then(module => ({ default: module.FeatureRequests })))
const Profile = lazy(() => import('./pages/Profile').then(module => ({ default: module.Profile })))
const ProfileForm = lazy(() => import('./pages/ProfileForm').then(module => ({ default: module.ProfileForm })))
const VerifyEmailChange = lazy(() => import('./pages/VerifyEmailChange').then(module => ({ default: module.VerifyEmailChange })))
const NotFound = lazy(() => import('./pages/NotFound').then(module => ({ default: module.NotFound })))
const FocusFinderAdminDashboard = lazy(() => import('./pages/FocusFinderAdminDashboard').then(module => ({ default: module.FocusFinderAdminDashboard })))
const UserAssessmentDetail = lazy(() => import('./pages/UserAssessmentDetail').then(module => ({ default: module.UserAssessmentDetail })))
const UserManagement = lazy(() => import('./pages/UserManagement').then(module => ({ default: module.UserManagement })))
const UserDetail = lazy(() => import('./pages/UserDetail').then(module => ({ default: module.UserDetail })))
const AdminPerformanceMonitoring = lazy(() => import('./pages/AdminPerformanceMonitoring').then(module => ({ default: module.AdminPerformanceMonitoring })))
const AdminWorkshopControls = lazy(() => import('./pages/AdminWorkshopControls').then(module => ({ default: module.AdminWorkshopControls })))
const AdminAIUsageTracking = lazy(() => import('./pages/AdminAIUsageTracking').then(module => ({ default: module.AdminAIUsageTracking })))
const OSCoursePreview = lazy(() => import('./pages/OSCoursePreview').then(module => ({ default: module.OSCoursePreview })))
const ShareYourWin = lazy(() => import('./pages/ShareYourWin').then(module => ({ default: module.ShareYourWinPage })))

// Component to handle page tracking
function PageTracker() {
  usePageTracking();
  return null;
}

function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
        <div className="min-h-screen bg-background">
          <ScrollToTop />
          <PageTracker />
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email-change" element={<VerifyEmailChange />} />
            <Route path="/guest-registration" element={<GuestRegistration />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <ProfileGuardedRoute>
                  <Dashboard />
                </ProfileGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools" element={
              <ProtectedRoute>
                <ProfileGuardedRoute>
                  <WorkshopGuardedRoute>
                    <Tools />
                  </WorkshopGuardedRoute>
                </ProfileGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/focus-finder" element={
              <ProtectedRoute>
                <FocusFinder />
              </ProtectedRoute>
            } />
            <Route path="/vision" element={
              <ProtectedRoute>
                <ProfileGuardedRoute>
                  <Vision />
                </ProfileGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/two-oars" element={
              <ProtectedRoute>
                <TwoOars />
              </ProtectedRoute>
            } />
            <Route path="/tools/customer-avatar-builder" element={
              <ProtectedRoute>
                <CustomerAvatarBuilder />
              </ProtectedRoute>
            } />
            <Route path="/tools/13-week-cashflow-plan" element={
              <ProtectedRoute>
                <CashflowPlan />
              </ProtectedRoute>
            } />
            <Route path="/tools/business-model-analysis" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <BusinessModelAnalysis />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/problem-size-matrix" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <ProblemSizeMatrix />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/customer-acquisition-model" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <CustomerAcquisitionModel />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/rice-model-risk-ratio" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <RiceModelRiskRatio />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/bottom-up-financial-projections" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <BottomUpFinancialProjections />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/weekly-team-compass" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <WeeklyTeamCompass />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/vision-plan" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <VisionPlan2Page />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/vision-plan-orig" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <VisionPlanPage />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/30-day-sprints" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <ThirtyDaySprintTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/daily-team-check-ins" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <DailyTeamCheckInsTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/weekly-leadership-sync" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <WeeklyLeadershipSyncTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/company-creed" element={
              <ProtectedRoute>
                <CompanyCreedTool />
              </ProtectedRoute>
            } />
            <Route path="/tools/company-goals-process" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <CompanyGoalsProcessTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/duplicate-yourself" element={
              <ProtectedRoute>
                <DuplicateYourselfTool />
              </ProtectedRoute>
            } />

            <Route path="/tools/top-performers-cheetah-vs-cat" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <TopPerformersCheetahVsCatTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/contrarian-interviewing-framework" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <ContrarianInterviewingTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/utilizing-va" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <UtilizingVATool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/job-description" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <JobDescriptionTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/100-day-plan" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <HundredDayPlanTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/role-results" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <RoleResultsTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/org-chart" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <OrgChartTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/set-working-culture" element={
              <ProtectedRoute>
                <SetWorkingCultureTool />
              </ProtectedRoute>
            } />
            <Route path="/tools/quarterly-goal-builder" element={
              <ProtectedRoute>
                <QuarterlyGoalBuilderTool />
              </ProtectedRoute>
            } />
            <Route path="/tools/ceo-mission-control-dashboard" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <CEOMissionControlDashboard />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/projects-database" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <ProjectsDatabaseTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/sop-builder-database" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <SOPBuilderDatabaseTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/task-database-outline" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <TaskDatabaseOutlineTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/tech-stack-evaluator" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <TechStackEvaluatorTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/flywheel-creation" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <FlywheelCreationTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/pricing-framework" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <PricingFrameworkTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/sales-enablement-library" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <SalesEnablementLibraryTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/sales-pipeline-process" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <SalesPipelineProcessTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/product-tick-sheet" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <ProductTickSheetTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/objection-rebuttal-matrix" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <ObjectionRebuttalMatrixTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/faq-matrix" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <FAQMatrixTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/growth-marketing-formula" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <GrowthMarketingFormulaTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/cashflow-acceleration-process" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <CashflowAccelerationTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/zero-based-budgeting" element={
              <ProtectedRoute>
                <ZeroBasedBudgetingTool />
              </ProtectedRoute>
            } />
            <Route path="/tools/vendor-discounts" element={
              <ProtectedRoute>
                <VendorDiscountsTool />
              </ProtectedRoute>
            } />
            <Route path="/tools/dynamic-pl" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <DynamicPLTool />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tools/dynamic-financial-dashboard" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <DynamicFinancialDashboard />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/models" element={
              <ProtectedRoute>
                <ProfileGuardedRoute>
                  <Models />
                </ProfileGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/tempo" element={
              <ProtectedRoute>
                <ProfileGuardedRoute>
                  <Tempo />
                </ProfileGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/people" element={
              <ProtectedRoute>
                <ProfileGuardedRoute>
                  <People />
                </ProfileGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/ops" element={
              <ProtectedRoute>
                <ProfileGuardedRoute>
                  <Ops />
                </ProfileGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/marketing-sales" element={
              <ProtectedRoute>
                <ProfileGuardedRoute>
                  <MarketingSales />
                </ProfileGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/finance" element={
              <ProtectedRoute>
                <ProfileGuardedRoute>
                  <Finance />
                </ProfileGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } />
            <Route path="/admin/error-reports" element={
              <AdminRoute>
                <AdminErrorReports />
              </AdminRoute>
            } />
            <Route path="/admin/analytics" element={
              <AdminRoute>
                <AdminAnalytics />
              </AdminRoute>
            } />
            <Route path="/admin/courses" element={
              <AdminRoute>
                <CourseManagement />
              </AdminRoute>
            } />
            <Route path="/admin/courses/new" element={
              <AdminRoute>
                <CourseForm />
              </AdminRoute>
            } />
            <Route path="/admin/courses/:id" element={
              <AdminRoute>
                <CourseDetails />
              </AdminRoute>
            } />
            <Route path="/admin/courses/:id/edit" element={
              <AdminRoute>
                <CourseForm />
              </AdminRoute>
            } />
            <Route path="/admin/videos" element={
              <AdminRoute>
                <VideoManager />
              </AdminRoute>
            } />
            <Route path="/admin/focus-finder" element={
              <AdminRoute>
                <FocusFinderAdminDashboard />
              </AdminRoute>
            } />
            <Route path="/admin/focus-finder/users/:userId" element={
              <AdminRoute>
                <UserAssessmentDetail />
              </AdminRoute>
            } />
            <Route path="/admin/users" element={
              <AdminRoute>
                <UserManagement />
              </AdminRoute>
            } />
            <Route path="/admin/users/:userId" element={
              <AdminRoute>
                <UserDetail />
              </AdminRoute>
            } />
            <Route path="/admin/ai-performance" element={
              <AdminRoute>
                <AdminPerformanceMonitoring />
              </AdminRoute>
            } />
            <Route path="/admin/ai-workshop-mode" element={
              <AdminRoute>
                <AdminWorkshopControls />
              </AdminRoute>
            } />
            <Route path="/admin/ai-usage" element={
              <AdminRoute>
                <AdminAIUsageTracking />
              </AdminRoute>
            } />
            <Route path="/courses" element={
              <ProtectedRoute>
                <Courses />
              </ProtectedRoute>
            } />
            <Route path="/os-course-preview" element={
              <ProtectedRoute>
                <OSCoursePreview />
              </ProtectedRoute>
            } />
            <Route path="/courses/:id" element={
              <ProtectedRoute>
                <CourseViewer />
              </ProtectedRoute>
            } />
            <Route path="/courses/:id/notes" element={
              <ProtectedRoute>
                <CourseNotes />
              </ProtectedRoute>
            } />
            <Route path="/video-vault" element={
              <ProtectedRoute>
                <WorkshopGuardedRoute>
                  <VideoVault />
                </WorkshopGuardedRoute>
              </ProtectedRoute>
            } />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/privacy" element={<PrivacyStatement />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/support" element={<Support />} />
            <Route path="/feature-requests" element={<FeatureRequests />} />
            <Route path="/share-your-win" element={<ShareYourWin />} />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/profile/edit" element={
              <ProtectedRoute>
                <ProfileForm />
              </ProtectedRoute>
            } />
            {/* Catch-all route for 404 errors */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>
      </ErrorBoundary>
    </AuthProvider>
  )
}

export default App 